<template>
  <div class="list-page">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      :editYear="selectedYear"
      @close="editDialog = false"
      @submit="getList"
    />

    <div class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Academics</div>
        <h1>Years</h1>
        <p>Manage academic years, departments, and internal notes.</p>
      </div>

      <div class="list-page-heading__actions">
        <v-btn
          v-if="checkRightStatus(33)"
          color="primary"
          depressed
          @click="add"
        >
          <v-icon left>mdi-plus</v-icon>
          New year
        </v-btn>
      </div>
    </div>

    <v-row class="list-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">
              mdi-calendar-range-outline
            </v-icon>
          </div>

          <div>
            <div class="list-metric__value">
              {{ years.length }}
            </div>

            <div class="list-metric__label">
              Total years
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">
              mdi-school-outline
            </v-icon>
          </div>

          <div>
            <div class="list-metric__value">
              {{ assignedDepartmentCount }}
            </div>

            <div class="list-metric__label">
              Departments assigned
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">
              mdi-note-text-outline
            </v-icon>
          </div>

          <div>
            <div class="list-metric__value">
              {{ yearsWithNotesCount }}
            </div>

            <div class="list-metric__label">
              Years with notes
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card
      :loading="loading"
      class="list-card"
      elevation="0"
    >
      <div class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search by title, department, or note"
          single-line
          hide-details
          clearable
        />

        <div class="list-toolbar__actions">
          <v-btn
            icon
            outlined
            color="primary"
            :loading="loading"
            aria-label="Refresh years"
            @click="getList"
          >
            <v-icon size="19">
              mdi-refresh
            </v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        class="list-table year-table"
        :headers="headers"
        :items="years"
        :search="search"
        :loading="loading"
        :sort-by="sortBy"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
      >
        <template v-slot:item="{ item }">
          <tr class="list-table-row pointer">
            <td>
              <div class="list-identity">
                <div class="list-identity__icon">
                  <v-icon color="primary" size="20">
                    mdi-calendar-range-outline
                  </v-icon>
                </div>

                <div class="list-identity__content">
                  <div class="list-identity__title">
                    {{ item.name }}
                  </div>

                  <div class="list-identity__subtitle">
                    Academic year
                  </div>
                </div>
              </div>
            </td>

            <td>
              <div
                v-if="item.Class"
                class="year-department"
              >
                <v-icon color="primary" size="16">
                  mdi-school-outline
                </v-icon>

                <div>
                  <strong>
                    {{ item.Class.name }}
                  </strong>

                  <span>
                    Department
                  </span>
                </div>
              </div>

              <div
                v-else
                class="year-department year-department--empty"
              >
                <v-icon size="16">
                  mdi-school-outline
                </v-icon>

                <div>
                  <strong>
                    No department
                  </strong>

                  <span>
                    Not assigned
                  </span>
                </div>
              </div>
            </td>

            <td>
              <div class="year-period">
                <v-icon size="16" color="primary">mdi-calendar-range</v-icon>
                <div v-if="item.startDate && item.endDate">
                  <strong>{{ formatDate(item.startDate) }} – {{ formatDate(item.endDate) }}</strong>
                  <span>Locks {{ formatDate(item.reportCardLockDate) }}</span>
                </div>
                <div v-else><strong>Dates not configured</strong><span>Report generation remains open</span></div>
              </div>
            </td>

            <td>
              <v-chip small label :color="statusColor(item)" :text-color="item.isReportCardLocked ? 'white' : undefined">
                <v-icon left small>{{ statusIcon(item) }}</v-icon>{{ statusLabel(item) }}
              </v-chip>
            </td>

            <td>
              <div
                v-if="item.note"
                class="year-note"
              >
                <v-icon size="16" color="primary">
                  mdi-note-text-outline
                </v-icon>

                <span>
                  {{ item.note }}
                </span>
              </div>

              <span
                v-else
                class="year-note-empty"
              >
                No note
              </span>
            </td>

            <td class="text-right">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Year actions"
                  >
                    <v-icon>
                      mdi-dots-horizontal
                    </v-icon>
                  </v-btn>
                </template>

                <v-list dense class="menu-button">
                  <v-list-item @click="manageCalendar(item)">
                    <v-list-item-icon><v-icon size="19">mdi-calendar-edit</v-icon></v-list-item-icon>
                    <v-list-item-title>Manage calendar</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="checkRightStatus(35)"
                    @click="editYear(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">
                        mdi-pencil-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title>
                      Edit
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="item.isReportCardLocked && (currentUser.isAdmin || currentUser.superAdmin)"
                    @click="unlockReportCards(item)"
                  >
                    <v-list-item-icon><v-icon size="19" color="warning darken-2">mdi-lock-open-variant-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Temporarily reopen reports</v-list-item-title>
                  </v-list-item>

                  <v-divider
                    v-if="
                      checkRightStatus(35) &&
                      checkRightStatus(36)
                    "
                  />

                  <v-list-item
                    v-if="checkRightStatus(36)"
                    @click="deleteYear(item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="error" size="19">
                        mdi-delete-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>

        <template v-slot:no-data>
          <div class="list-empty-state">
            <v-icon
              size="44"
              color="grey lighten-1"
            >
              mdi-calendar-range-outline
            </v-icon>

            <strong>
              No years found
            </strong>

            <span>
              Add your first academic year to organize departments and classes.
            </span>

            <v-btn
              v-if="checkRightStatus(33)"
              color="primary"
              depressed
              small
              @click="add"
            >
              <v-icon left small>
                mdi-plus
              </v-icon>

              Add year
            </v-btn>
          </div>
        </template>

        <template v-slot:no-results>
          <div class="list-empty-state">
            <v-icon
              size="44"
              color="grey lighten-1"
            >
              mdi-magnify-close
            </v-icon>

            <strong>
              No matching years
            </strong>

            <span>
              No years matched "{{ search }}".
            </span>

            <v-btn
              text
              color="primary"
              small
              @click="search = null"
            >
              Clear search
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <v-data-iterator
        v-else
        :items="years"
        :search="search"
        :loading="loading"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :footer-props="{
          'items-per-page-options': [10, 25],
        }"
        class="list-mobile"
      >
        <template v-slot:default="{ items }">
          <div
            v-for="item in items"
            :key="item.id"
            class="list-mobile-card"
          >
            <div class="list-mobile-card__top">
              <div class="list-identity__icon">
                <v-icon color="primary" size="20">
                  mdi-calendar-range-outline
                </v-icon>
              </div>

              <div class="list-mobile-card__identity">
                <strong>
                  {{ item.name }}
                </strong>

                <span>
                  {{ item.Class ? item.Class.name : "No department" }}
                </span>
              </div>

              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Year actions"
                  >
                    <v-icon>
                      mdi-dots-horizontal
                    </v-icon>
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item @click="manageCalendar(item)">
                    <v-list-item-icon><v-icon size="19">mdi-calendar-edit</v-icon></v-list-item-icon>
                    <v-list-item-title>Manage calendar</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="checkRightStatus(35)"
                    @click="editYear(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">
                        mdi-pencil-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title>
                      Edit
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="item.isReportCardLocked && (currentUser.isAdmin || currentUser.superAdmin)"
                    @click="unlockReportCards(item)"
                  >
                    <v-list-item-icon><v-icon size="19" color="warning darken-2">mdi-lock-open-variant-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Temporarily reopen reports</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="checkRightStatus(36)"
                    @click="deleteYear(item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="error" size="19">
                        mdi-delete-outline
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div class="year-mobile-note">
              <span>Report-card window</span>
              <strong>{{ item.startDate && item.endDate ? `${formatDate(item.startDate)} – ${formatDate(item.endDate)}` : "Dates not configured" }}</strong>
              <v-chip class="year-mobile-status" small label :color="statusColor(item)" :text-color="item.isReportCardLocked ? 'white' : undefined">{{ statusLabel(item) }}</v-chip>
            </div>

            <div class="year-mobile-note">
              <span>
                Note
              </span>

              <strong>
                {{ item.note || "No note" }}
              </strong>
            </div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="list-empty-state">
            <v-icon
              size="44"
              color="grey lighten-1"
            >
              mdi-calendar-range-outline
            </v-icon>

            <strong>
              No years found
            </strong>

            <span>
              Add your first academic year to organize departments and classes.
            </span>
          </div>
        </template>
      </v-data-iterator>
    </v-card>
  </div>
</template>

<script>
import yearService from "../service";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "year-list",

  components: {
    Create,
    Edit,
  },

  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedYear: null,

      headers: [
        {
          text: "Title",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Department",
          align: "left",
          value: "Class.name",
          sortable: true,
          width: "220px",
        },
        {
          text: "Academic period",
          align: "left",
          value: "startDate",
          sortable: true,
          width: "235px",
        },
        {
          text: "Reports",
          align: "left",
          value: "reportCardStatus",
          sortable: true,
          width: "150px",
        },
        {
          text: "Notes",
          align: "left",
          value: "note",
          sortable: true,
        },
        {
          text: "",
          align: "right",
          value: "action",
          width: "64px",
          sortable: false,
        },
      ],

      years: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "createdAt",
      itemsPerPage: 10,
    };
  },

  created() {
    this.getList();
    document.title = "taleem - Year List";
  },

  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
    ]),

    assignedDepartmentCount() {
      return this.years.filter((year) => year.Class).length;
    },

    yearsWithNotesCount() {
      return this.years.filter((year) => {
        return year.note && String(year.note).trim();
      }).length;
    },
  },

  methods: {
    manageCalendar(year) {
      this.$router.push({ name: "year-calendar", params: { yearId: year.id } });
    },
    formatDate(value) {
      if (!value) return "—";
      const normalized = String(value).slice(0, 10);
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${normalized}T00:00:00Z`));
    },

    statusLabel(year) {
      if (year.reportCardStatus === "temporarily_unlocked") return "Temporarily open";
      if (year.isReportCardLocked) return "Locked";
      if (year.reportCardStatus === "dates_not_configured") return "Not configured";
      return "Open";
    },

    statusColor(year) {
      if (year.isReportCardLocked) return "grey darken-1";
      if (year.reportCardStatus === "temporarily_unlocked") return "warning lighten-4";
      if (year.reportCardStatus === "dates_not_configured") return "grey lighten-3";
      return "green lighten-5";
    },

    statusIcon(year) {
      return year.isReportCardLocked ? "mdi-lock-outline" : "mdi-lock-open-variant-outline";
    },

    async unlockReportCards(year) {
      const result = await this.$swal({
        title: "Temporarily reopen report cards?",
        text: `${year.name} is closed. Administrators can reopen generation for the configured temporary unlock period.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Reopen temporarily",
      });
      if (!result.value) return;
      try {
        await yearService.unlockReportCards(year.id);
        this.$swal("Reopened", "Report-card generation is temporarily available.", "success");
        await this.getList();
      } catch (error) {
        this.$swal("Unable to reopen", error?.response?.data?.message || error?.data?.message || error.message, "error");
      }
    },

    add() {
      this.createDialog = true;
    },

    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      return yearService
        .getAll()
        .then((response) => {
          this.years = response.data || [];
          this.loading = false;

          return response;
        })
        .catch((error) => {
          this.loading = false;

          this.$swal({
            title: "Year List",
            text:
              error &&
              error.data &&
              error.data.message
                ? error.data.message
                : "Unable to load the year list.",
            icon: "error",
          });
        });
    },

    editYear(year) {
      this.selectedYear = year;
      this.editDialog = true;
    },

    deleteYear(year) {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${year.name}. This action cannot be reverted.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          yearService
            .delete(year.id)
            .then((response) => {
              if (response.data) {
                this.$swal(
                  "Deleted!",
                  "Year has been deleted.",
                  "success"
                );

                this.getList();
              }
            })
            .catch((error) => {
              this.$swal({
                title: "Delete Year",
                text:
                  error &&
                  error.data &&
                  error.data.message
                    ? error.data.message
                    : "Unable to delete this year.",
                icon: "error",
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
/* Shared page, heading, metrics, card, toolbar, search, table,
   mobile cards, and empty-state styles come from app.scss. */

.year-department {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 180px;
}

.year-department > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.year-department strong {
  overflow: hidden;
  color: #34433a;
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.year-department span {
  margin-top: 2px;
  color: #899489;
  font-size: 9px;
}

.year-department--empty strong,
.year-department--empty span {
  color: #8a968d;
}

.year-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  max-width: 480px;
}

.year-period {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 215px;
}

.year-period > div { display: flex; flex-direction: column; }
.year-period strong { color: #34433a; font-size: 11px; }
.year-period span { margin-top: 2px; color: #899489; font-size: 9px; }
.year-mobile-status { align-self: flex-start; margin-top: 7px; }

.year-note span {
  overflow: hidden;
  color: #4f5d53;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.year-note-empty {
  color: #8a968d;
  font-size: 11px;
}

.year-mobile-note {
  display: flex;
  margin: 14px 0 0 51px;
  padding-top: 11px;
  border-top: 1px solid #f0f2f0;
  flex-direction: column;
}

.year-mobile-note span {
  margin-bottom: 3px;
  color: #919b92;
  font-size: 10px;
  text-transform: uppercase;
}

.year-mobile-note strong {
  overflow: hidden;
  color: #445047;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .year-table {
    overflow-x: auto;
  }

  .year-table ::v-deep table {
    min-width: 1080px;
  }
}
</style>
